import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Library,
  FileText,
  AlertTriangle,
  Settings,
  User,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { hasAccessToRoute } from '@/shared/config/permissions'
import { cn } from '@/lib/utils'

interface NavItem {
  path: string
  labelKey: string
  icon: React.ComponentType<{ className?: string }>
}

interface NavSection {
  titleKey?: string
  items: NavItem[]
}

const ADMIN_NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { path: '/dashboard', labelKey: 'adminNav.dashboard', icon: LayoutDashboard },
    ],
  },
  {
    titleKey: 'adminNav.management',
    items: [
      { path: '/formateurs', labelKey: 'adminNav.formateurs', icon: Users },
      { path: '/etudiants', labelKey: 'adminNav.etudiants', icon: GraduationCap },
      { path: '/programmes', labelKey: 'adminNav.programmes', icon: BookOpen },
      { path: '/administration', labelKey: 'adminNav.referentiels', icon: Library },
    ],
  },
  {
    titleKey: 'adminNav.supervision',
    items: [
      { path: '/logs', labelKey: 'adminNav.logs', icon: FileText },
      { path: '/erreurs', labelKey: 'adminNav.errors', icon: AlertTriangle },
      { path: '/systeme', labelKey: 'adminNav.system', icon: Settings },
    ],
  },
  {
    titleKey: 'adminNav.account',
    items: [
      { path: '/profil', labelKey: 'adminNav.profile', icon: User },
    ],
  },
]

interface AdminSidebarProps {
  onNavigate?: () => void
  isDark?: boolean
  onToggleDark?: () => void
}

export function AdminSidebar({ onNavigate, isDark, onToggleDark }: AdminSidebarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3 pt-4">
        {ADMIN_NAV_SECTIONS.map((section, sectionIdx) => (
          <div key={sectionIdx}>
            {section.titleKey && (
              <div className="px-3 pt-4 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-300">
                  {t(section.titleKey)}
                </p>
              </div>
            )}
            {section.items
              .filter((item) => hasAccessToRoute(user?.role, item.path))
              .map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                      isActive
                        ? 'bg-gradient-to-r from-primary/12 to-primary/6 text-primary shadow-sm'
                        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-neutral-200 p-3 space-y-2">
        {/* Dark mode toggle */}
        {onToggleDark && (
          <button
            onClick={onToggleDark}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-all hover:bg-neutral-100 hover:text-neutral-900"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{isDark ? t('adminNav.lightMode', 'Mode clair') : t('adminNav.darkMode', 'Mode sombre')}</span>
          </button>
        )}

        {user && (
          <div className="rounded-lg bg-neutral-50 px-3 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-800 text-sm font-bold text-white shadow-sm">
                {user.prenom?.[0]}{user.nom?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900">{user.prenom}</p>
                <p className="truncate text-sm text-neutral-700">{user.nom}</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
        >
          <LogOut className="h-4 w-4" />
          <span>{t('common.logout', 'Déconnexion')}</span>
        </button>
      </div>
    </div>
  )
}
